import { FormArray, FormGroup } from '@angular/forms';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { IScoreWeight } from 'src/app/interfaces/self-serve/isimulation-score';

function isMessagingEnabled(form: FormGroup): boolean {
  const sectionWeights = form.get(
    `${SimulationConstants.SCORING_SECTION}.${SimulationConstants.SCORING_WEIGHTS_SECTION}`
  )?.value as IScoreWeight[] | undefined;

  return !(sectionWeights ?? []).find(
    (weight) => weight.section === SimulationConstants.MESSAGING_SECTION
  )?.disabled;
}

function getFactSections(form: FormGroup): FormArray | null {
  return form.get(
    `${SimulationConstants.SCORING_SECTION}.${SimulationConstants.MESSAGING_THEME_SECTION}.${SimulationConstants.FACT_SECTIONS_SECTION}`
  ) as FormArray | null;
}

/** True when a theme (fact section) has no facts, or any of its facts has
 * blank message text. A theme with zero facts is just as incomplete as one
 * with a blank fact — both leave nothing for the AI to score against. */
function isFactSectionIncomplete(section: FormGroup): boolean {
  const facts = section.get('facts') as FormArray | null;
  if (!facts || facts.length === 0) return true;

  return facts.controls.some(
    (fact) => !(fact as FormGroup).get('rawText')?.value?.toString().trim()
  );
}

/** Self-authoring's "no themes at all" check — only applies while Messaging
 * is switched on. Kept separate from hasIncompleteMessagingTheme so the UI
 * can show a distinct "add a theme" tooltip instead of the generic
 * "facts cannot be empty" one. */
export function hasNoMessagingThemes(form: FormGroup): boolean {
  if (!isMessagingEnabled(form)) return false;

  const factSections = getFactSections(form);
  return !factSections || factSections.length === 0;
}

/** Self-authoring's "an existing theme is incomplete" check — only applies
 * while Messaging is switched on, and only once at least one theme exists
 * (hasNoMessagingThemes covers the zero-theme case). Catches a theme with
 * zero facts and a fact left blank. */
export function hasIncompleteMessagingTheme(form: FormGroup): boolean {
  if (!isMessagingEnabled(form)) return false;

  const factSections = getFactSections(form);
  if (!factSections || factSections.length === 0) return false;

  return factSections.controls.some((section) =>
    isFactSectionIncomplete(section as FormGroup)
  );
}
